import { NICE } from '../core/doc.js';
import { LOGO_URL, watermarkRectAtTime } from '../export/watermark.js';
import { gzLocal, setHover } from '../scene/gizmo.js';
import { addObject, getSel } from '../scene/objects.js';
import { panelDescs } from '../scene/panels.js';
import { fitStage } from '../scene/renderer.js';
import { isSiteLike } from '../scene/cameras.js';
import { MOTION_CTA, canUseMotion, canUseScreenArray, canUseVideo, videoWatermarkOn } from '../services/limits.js';
import { showTrial } from './trial.js';
import { showAddModel } from './add-model.js';
import { on, requestRender } from '../state/bus.js';
import { hasMotion } from '../state/timeline.js';
import { stopPreview } from '../state/moves.js';
import { $, S, el } from '../state/store.js';
import { fitPhoto, paintHandles, setRecropVisible } from '../ui/calibrate-view.js';
import { ICONS } from '../ui/controls.js';
import { openMask } from '../ui/mask-editor.js';
import { applySiteTransform, setSiteZoom, siteFit } from '../ui/site-view.js';
import { applyFlatTransform, flatFit, flatZoomable, setFlatZoom } from './flat-view.js';
import { toast } from '../ui/toast.js';
import { renderLeft } from './rail-left.js';
import { renderRight } from './rail-right.js';
import { setTimelineStage } from './timeline-view.js';

/* One path per session for the preview mark. Not the export's seed — that one
   is chosen per export, so nothing here could match a file that does not
   exist yet. See paintOneWatermark. */
const PREVIEW_SEED = (Math.random() * 0x7fffffff) | 0;

/* Everything around the viewport: the stage stepper, the view tabs, the
   floating tool palette and the hint line. Grouped because they all answer
   the same question — what mode is the app in — and all of them repaint
   together. The eye/screen/output readout that used to live in a floating
   HUD here is in Master Settings' Calibration panel now (ui/rail-right.js)
   — the same numbers, without permanently covering a corner of the render. */

/* ═══════════════════════════════════════════════════════════
   STAGE SWITCH
   ═══════════════════════════════════════════════════════════ */
/* Motion is licensed (services/motion.js). Refused HERE rather than only on
   the tab, because goStage is called from several places — the tab, keyboard
   shortcuts, and project load — and a gate that lives on one button is a gate
   with three ways around it. Explains rather than failing silently: a stage
   that simply does not switch reads as a bug. */
export function goStage(st){
  if(st === 'animate'){
    const gate = canUseMotion();
    if(!gate.ok){ toast(gate.reason, true, { text: MOTION_CTA, onClick: showTrial }); syncMotionLock(); return; }
  }
  /* A move preview is playing values straight onto an object without writing
     anything, so it has to let go before the workspace changes under it. The
     bus has no stage event to subscribe to — see the note in state/moves.js —
     so it is stopped here, at the one place a stage actually changes. */
  stopPreview();
  S.stage = st;
  document.querySelectorAll('.step').forEach(b=>b.classList.toggle('on', b.dataset.stage===st));
  const calib = st==='calib';
  // Calibrate is its own focused mode now — the photo and its settings
  // (#calibPanel, built by ui/rail-left.js) fill the stage on their own, so
  // the dock has nothing to show and is collapsed rather than left as an
  // empty strip beside it. #stage and #railR are auto-placed by DOM order
  // (only .topbar has an explicit grid-column), so hiding the rail via
  // display:none is required too — otherwise it would still occupy the
  // grid's second column slot and leave a blank gap beside #stage.
  document.querySelector('.app').classList.toggle('calib-mode', calib);
  /* Motion is a stripped-back version of the same workspace, not a different
     screen: same viewport, same view tabs, same canvas. What changes is what
     is offered — the object tools go away (nothing is being built here) and
     the inspector drops to the seven properties that can actually be
     keyframed. The timeline takes the room that frees up. */
  const anim = st === 'animate';
  document.querySelector('.app').classList.toggle('anim-mode', anim);
  $('#railR').classList.toggle('hidden', calib);
  $('#viewCalib').classList.toggle('hide', !calib);
  $('#viewtabs').classList.toggle('hidden', calib);
  $('#hint').classList.toggle('hidden', calib);
  /* The palette stays in Motion, minus the half of it that builds things.
     Select/Move/Rotate/Scale and the world/local toggle are how a keyframe
     gets its value in the first place — animating position means dragging
     the object at each playhead position, so sending someone back to Design
     to pick up the move tool, then forward again to use it, made the one
     workflow Motion exists for the awkward one. Add and Mask do go: nothing
     is built here, and those were the tools that genuinely did not belong. */
  $('#palette').classList.toggle('hidden', calib);
  document.querySelector('.palette-add').classList.toggle('hidden', anim);
  document.querySelector('.palette-mask').classList.toggle('hidden', anim);
  setTimelineStage(st);
  syncGizmoBar();
  if(calib){
    $('#view3d').classList.add('hide');
    $('#viewSite').classList.add('hide');
    $('#viewSplit').classList.add('hide');
    // Re-sync #drop vs #calibCard against S.photo on every entry, not just
    // when a photo first loads — the calibfoot's Back button can hide
    // #calibCard without clearing S.photo, so re-entering Calibrate (e.g.
    // from the topbar, after already having a photo) has to recover the
    // right view rather than trust whichever one was left showing.
    const hasPhoto = !!S.photo;
    $('#drop').classList.toggle('hidden', hasPhoto);
    $('#calibCard').classList.toggle('hidden', !hasPhoto);
    /* Same reason: a project can be opened or started from anywhere, not just
       through loadPhoto, and "Adjust crop" must not survive into a photo it
       has no original for. */
    setRecropVisible();
    // fitPhoto() sizes the image against the spine/footer/panel's real
    // rendered extent (see its own comment) — those are all 0 while
    // #calibCard is hidden, so the size it computed then is stale the
    // moment the card is visible again.
    // Not paintHandles directly — requestAnimationFrame calls its callback
    // with a timestamp, which paintHandles(dirty=true) would read as a
    // truthy dirty flag and use to silently un-solve an untouched
    // calibration (see that function's own comment on what dirty means).
    if(hasPhoto){ fitPhoto(); requestAnimationFrame(()=>paintHandles(false)); }
  }else{
    // First time reaching Design this session, S.view is still the
    // unresolved default (null) — this is reached right after locking the
    // angle, so On-site is the default once a reference photo exists: it's
    // the first look at how the build actually lands on the real building,
    // not just the flat deliverable in isolation. Split (Orbit + Flat
    // together) is the default for the manual-setup path instead, since
    // there's no photo there to preview against. A real view name never
    // equals null, so this only ever resolves once — an explicit later
    // choice always sticks.
    if(S.view==null) S.view = S.photo ? 'site' : 'split';
    // Still guard against a combination that's become invalid since — a
    // photo added after starting manually, or vice versa. setView() itself
    // already refuses to switch INTO the wrong one of these for the
    // current S.photo state, so this only ever needs to correct away from
    // one that's already stuck.
    if(S.view==='site' && !S.photo) S.view='split';
    setView(S.view);
  }
  // Both rails directly rather than renderRails(), which lives on the facade —
  // reaching back for it would make rails.js depend on this file and this file
  // on rails.js, for nothing.
  renderLeft(); renderRight();
}
/* Only the buttons that actually name a stage. Export shares this group and
   this styling but is an action (see index.html) — without the filter it
   would call goStage(undefined) and blank the app. */
document.querySelectorAll('.step[data-stage]').forEach(b=>b.onclick=()=>{ if(!b.disabled) goStage(b.dataset.stage); });

/* The Motion tab's locked state.

   Two different unavailable-nesses live on this one button and they must not
   be conflated. The native `disabled` attribute means "you have not calibrated
   a screen yet" — it ships set in index.html and is cleared by
   ui/calibrate-view.js and services/projects.js once S.solved. This is the
   other one: calibrated, but unlicensed.

   So this uses .btn--locked + aria-disabled and NEVER the native attribute,
   for the reason ui/export-view.js already argues about its own cards: a
   disabled element swallows its click, and a lock that cannot be clicked can
   never say why it is locked. The click lands, goStage refuses it, and the
   toast explains. */
export function syncMotionLock(){
  const b = document.querySelector('.step[data-stage="animate"]');
  if(!b) return;
  const gate = canUseMotion();
  b.classList.toggle('btn--locked', !gate.ok);
  b.setAttribute('aria-disabled', String(!gate.ok));
  b.title = gate.ok ? '' : gate.reason;
}
on('motion:changed', syncMotionLock);
on('auth:changed', syncMotionLock);
syncMotionLock();

/* UNSAVED WORK, SHOWN. A small blue dot on Projects whenever S.dirty is true —
   the same flag the "leave site?" warning reads, so the two can never disagree.
   For a cloud project autosave clears it a couple of seconds after each change;
   a local or never-saved one keeps it until it is saved.

   S.dirty is set in a dozen places across the services, so it is watched rather
   than each of them being taught to repaint: the property becomes an accessor
   that paints the button whenever it is written. */
{
  let dirty = !!S.dirty;
  const paintDirty = () => {
    const b = document.getElementById('btnProjects');
    if(!b) return;
    b.classList.toggle('btn--unsaved', dirty);
    b.title = dirty ? 'Projects · this project has unsaved changes' : 'Projects';
  };
  Object.defineProperty(S, 'dirty', {
    configurable: true, enumerable: true,
    get(){ return dirty; },
    set(v){ const d = !!v; if(d === dirty) return; dirty = d; paintDirty(); }
  });
  paintDirty();
}

/* The screen-array door on the start screen, locked the same way the Motion
   stage tab is. Same licence, same two events, and the same reasoning: the
   refusal lives in arraySetup so it cannot be walked round, and this only makes
   the button honest before it is pressed. */
export function syncArrayLock(){
  const gate = canUseScreenArray();
  for(const id of ['btnArrayPhoto', 'btnArray']){
    const b = document.getElementById(id);
    if(!b) continue;
    b.classList.toggle('btn--locked', !gate.ok);
    b.setAttribute('aria-disabled', String(!gate.ok));
    b.title = gate.ok ? '' : gate.reason;
  }
}
on('motion:changed', syncArrayLock);
on('auth:changed', syncArrayLock);
syncArrayLock();

/* The Video tool, locked the same way. A third event here that the other two
   do not need: this gate also opens by BUYING credits, and that lands as an
   entitlements change rather than a motion grant or a sign-in. */
export function syncVideoLock(){
  const b = document.getElementById('toolVideo');
  if(!b) return;
  const gate = canUseVideo();
  b.classList.toggle('btn--locked', !gate.ok);
  b.setAttribute('aria-disabled', String(!gate.ok));
  b.title = gate.ok ? NICE.video : gate.reason;
}
on('motion:changed', syncVideoLock);
on('auth:changed', syncVideoLock);
on('entitlements:changed', syncVideoLock);

/* The flat "deliverable" view and the PNG export share a render path
   (scene/renderer.js), so the on-screen watermark mirrors the export's: what
   you see here is what an ungated export actually looks like, not a
   surprise at download time. Orbit and Flat share the same #fitbox/#view3d
   DOM (only the active camera differs — see scene/cameras.js), so this has
   to check S.view itself rather than just element visibility. A DOM overlay,
   not baked into the WebGL render: it's static and the flat view's draw()
   loop already runs every frame during interaction, so compositing it into
   the render buys nothing and costs a canvas 2D pass per frame.

   Points background-image straight at the source file and repositions it
   with plain left/top/width/height — no canvas, no toDataURL. That used to
   go through a canvas (matching export/png.js's compositing, which really
   does need one), but re-encoding the logo's own detailed pixel content to
   PNG on every reposition measured at 100–250ms regardless of how small
   the canvas was, which is exactly what "lags behind while zooming" was:
   the image's pixels never change between reposition, only where it sits,
   so there was never anything to re-encode in the first place. */
function paintOneWatermark(el, fb, on, zoom = 1, sizeRef){
  el.classList.toggle('hidden', !on);
  if(!on) return;
  const boxW = fb.clientWidth, boxH = fb.clientHeight;
  if(!boxW || !boxH) return;
  /* The frame's own SCREEN area, not the whole box: standalone Flat's live
     zoom (S.flatZoom, scene/cameras.js's updateFlatCam) shows a
     screen-centred zoomed crop of the calibrated screen inside this same
     fixed-size box — so the box's edges stop being the screen's edges the
     moment zoom != 1. The screen's own rendered rect is exactly
     box-size × zoom, centred (the same "screen-centred" zoom
     updateFlatCam itself describes) — the mark's rect is computed within
     THAT sub-frame, then offset back into the box's own coordinates. */
  const screenW = boxW * zoom, screenH = boxH * zoom;
  /* Panning slides that same rect. S.flatPan is in METRES on the screen
     plane (it feeds the frustum directly), so it converts at the rect's own
     pixels-per-metre; moving the frustum window right shows content further
     left, hence the sign, and Y is inverted between the two spaces. */
  const pan = (zoom > 1 && S.flatPan) ? S.flatPan : { x: 0, y: 0 };
  const screenX = (boxW - screenW) / 2 - pan.x * (screenW / Math.max(1e-6, S.screen.w));
  const screenY = (boxH - screenH) / 2 + pan.y * (screenH / Math.max(1e-6, S.screen.h));
  /* Sized off the reference frame when one is given — a corner's second panel
     takes the first's size so the pair carries one mark, not two of different
     sizes. Positioned in its own frame either way. */
  /* Positioned at the PLAYHEAD, not at a fixed spot, because the export's
     mark moves every few seconds and this pane's whole job is to show what an
     ungated export looks like. A preview that kept it bottom-centre would be
     telling a lie the moment somebody scrubbed past three seconds.

     One seed for the session rather than the export's own: the export picks a
     fresh seed each time, so no preview could match a file that does not
     exist yet. What this shows is the behaviour — it starts where a still
     puts it and moves on — which is the part worth knowing in advance. */
  const rect = watermarkRectAtTime(screenW, screenH,
                             sizeRef ? sizeRef.w : screenW, sizeRef ? sizeRef.h : screenH,
                             S.playhead, PREVIEW_SEED);

  el.style.left = (screenX + rect.x) + 'px';
  el.style.top = (screenY + rect.y) + 'px';
  el.style.width = rect.w + 'px';
  el.style.height = rect.h + 'px';
  el.style.backgroundImage = `url(${LOGO_URL})`;
}
/* A WALL'S DELIVERABLE IS ONE FILE PER SCREEN, and the export stamps EVERY one
   (export/multipanel.js): each tile carries its own mark, all the same size —
   taken from the first tile — and all on the same beat, each placed within its
   own frame. So this pane has to show a mark on every tile. It used to paint
   the single-screen mark once across the whole wall, which put one logo
   straddling a seam that no file contains and left every other screen
   previewing clean, when each of their files carries one.

   Pooled elements, made as the wall needs them and hidden when it does not,
   because a wall's tile count changes as it is edited and these repaint on
   every playhead move. Children of the pane, like the single mark, so they ride
   Flat's zoom with the render.

   Placed from the tile's own metres, the same numbers the export renders from
   (panelDescs), converted at the pane's pixels-per-metre. */
const wallMarks = new Map();          // pane element → its pooled mark elements
function paintWallWatermarks(fb, on){
  let pool = wallMarks.get(fb);
  if(!pool){ pool = []; wallMarks.set(fb, pool); }
  const descs = on ? panelDescs() : [];
  const boxW = fb.clientWidth, boxH = fb.clientHeight;
  const ok = descs.length && boxW && boxH;
  while(ok && pool.length < descs.length){
    const d = document.createElement('div');
    d.className = 'watermark hidden';
    d.style.backgroundImage = `url(${LOGO_URL})`;
    fb.appendChild(d);
    pool.push(d);
  }
  pool.forEach(d => d.classList.add('hidden'));
  if(!ok) return;
  const sx = boxW / Math.max(1e-6, S.screen.w), sy = boxH / Math.max(1e-6, S.screen.h);
  const refW = descs[0].w * sx, refH = descs[0].h * sy;
  descs.forEach((desc, i) => {
    const w = desc.w * sx, h = desc.h * sy;
    const rect = watermarkRectAtTime(w, h, refW, refH, S.playhead, PREVIEW_SEED);
    const el = pool[i];
    el.classList.remove('hidden');
    el.style.left = (boxW/2 + (desc.pos.x - desc.w/2) * sx + rect.x) + 'px';
    el.style.top = (boxH/2 - (desc.pos.y + desc.h/2) * sy + rect.y) + 'px';
    el.style.width = rect.w + 'px';
    el.style.height = rect.h + 'px';
  });
}
function paintFlatWatermark(){
  /* Shows what was CHOSEN on the Export screen — the watermark switch — not
     what an account happens to hold. It used to ask "would this be marked for
     you?", which meant a licence, or ten spare credits, quietly turned the
     preview's mark off for exactly the person looking after the app, with
     nothing anywhere saying why.

     Only where a mark can appear at all: a still is free and clean, and there
     is only a video if something is keyframed. Without the hasMotion test this
     pane would put a watermark over a project whose PNG never carries one.

     NOT conditional on the account. It used to also require that the account
     could export video, on the reasoning that somebody who cannot yet make a
     marked file should not be shown one. That was backwards: this pane is
     where somebody sees what an animation looks like once it leaves the app,
     and for a visitor with nothing bought that is the mark. Hiding it from
     exactly the people it is a preview FOR meant the first they saw of it was
     the export. One keyframe, on a signed-out visitor's screen, is enough. */
  const wm = hasMotion() && videoWatermarkOn();
  /* No zoom argument any more. Flat's zoom scales #fitbox itself, and this
     mark is a CHILD of that element, so it is carried along with the render
     and the box is the screen rect again at every zoom — which is exactly
     what the default of 1 means here. */
  const wall = !!S.screenArray;
  paintOneWatermark($('#flatWatermark'), $('#fitbox'), S.view === 'flat' && wm && !wall);
  paintWallWatermarks($('#fitbox'), S.view === 'flat' && wm && wall);
  // Split's right pane previews the same deliverable Flat does, so it gets
  // the same honest "this is what an ungated export looks like" mark. It
  // never zooms — drawSplit() (scene/renderer.js) always calls
  // updateFlatCam with no zoom argument for this pane — so the default
  // zoom of 1 (the whole box IS the screen) is always right here.
  paintOneWatermark($('#splitWatermark'), $('#fitboxSplitR'), S.view === 'split' && wm && !wall);
  paintWallWatermarks($('#fitboxSplitR'), S.view === 'split' && wm && wall);
  /* The second panel of a corner screen. Its element has been in the markup
     since the pane was added and nothing ever painted it, so half of a corner
     deliverable previewed unmarked while the export stamped BOTH — the one
     direction this must never get wrong, since the preview's whole job is to
     say what an ungated export looks like.

     Zoom stays 1: flat zoom is forced off in corner mode (see drawCorner), so
     this pane's box always IS its screen. */
  const first = $('#fitbox');
  paintOneWatermark($('#cornerWatermark'), $('#fitboxCornerR'),
                    S.view === 'flat' && !!S.cornerPanel && wm, 1,
                    first ? { w: first.clientWidth, h: first.clientHeight } : null);
}
on('entitlements:changed', paintFlatWatermark);
/* Flipped on the Export screen. */
on('export:mark', paintFlatWatermark);
/* The first keyframe is what makes there be a video at all, and nothing else
   about adding one moves the playhead or the stage. */
on('history:pushed', paintFlatWatermark);
// A Motion licence clears the mark as well (services/limits.js), so the
// preview has to answer to that event too — otherwise the mark would linger
// on screen until something unrelated happened to repaint it.
on('motion:changed', paintFlatWatermark);
// #fitbox's size is only ever final once fitStage() has run — anything that
// changes it (view switch, window resize, UI scale, a settings change) goes
// through fitStage(), so re-painting from the one event it emits (rather
// than every call site that happens to trigger it) can't drift out of sync
// with a spot this doesn't know about yet.
on('stage:fit', paintFlatWatermark);
/* The mark moves with the clip, so it has to be repainted as the playhead
   does — on a scrub and on every frame of playback. It is a style write on
   one element against an event the timeline already emits, which is the same
   budget as the position hint next to it. */
on('playhead:moved', paintFlatWatermark);
// Scroll-zooming Flat (ui/interaction.js) fires 'view:zoomed' many times a
// second — a fixed setTimeout debounce here first made the watermark visibly
// lag a beat behind the content while scrolling, since requestRender()'s own
// draw() has no such delay. Coalescing into the next animation frame instead
// keeps this on the same cadence as the render loop (also rAF-driven, see
// renderer.js's loop()) — at most one repaint per frame, not one per event,
// but never more than a frame behind.
let zoomWmScheduled = false;
on('view:zoomed', () => {
  if(zoomWmScheduled) return;
  zoomWmScheduled = true;
  requestAnimationFrame(() => { zoomWmScheduled = false; paintFlatWatermark(); });
});

/* .hint sits a fixed 30px gap to the right of the palette's own right edge —
   measured from the palette's actual rendered width (it shrink-wraps to
   whichever row's label is widest, see buildPalette/app.css) rather than a
   hardcoded pixel value that would drift the moment a label changes. The
   palette's own content is static once built, but its rendered width can
   still change size out from under it — a UI-scale change is the one that
   matters here — so this rides the same 'stage:fit' event the watermark
   repaint does, for the same reason: whatever changes the layout already
   goes through fitStage(), so listening to the one event it emits can't
   drift out of sync the way a hand-picked list of call sites could. */
function positionHint(){
  const p = $('#palette'), h = $('#hint');
  h.style.left = (p.offsetLeft + p.offsetWidth + 30) + 'px';
}
on('stage:fit', positionHint);

export function setView(v){
  if(v==='site' && !S.photo){ toast('The on-site preview needs a reference photo.', true); return; }
  S.view = v;
  /* Scoped to THE view switcher, not to every element wearing its class.
     Calibrate's Photo/Plan tabs borrow the .viewtabs look, and while these
     selectors were class-wide they were claimed by this handler too: their
     data-view is undefined, so `undefined === undefined` lit both of them at
     once, and the onclick below overwrote the one they had of their own. */
  document.querySelectorAll('#viewtabs button').forEach(b=>b.classList.toggle('on', b.dataset.view===v));
  /* Split is offered on every path now. It used to be hidden as soon as a photo
     existed, because its right pane was the Flat deliverable and the answer to
     "what belongs beside Orbit" was already taken by On-site. The pane follows
     the project instead: Flat when there is no photograph to composite onto,
     the On-site mockup when there is. Either way the tab means the same thing —
     the scene and its result, side by side — so there is nothing left to hide. */
  const splitTab = $('#viewtabs button[data-view="split"]');
  if(splitTab) splitTab.classList.remove('hidden');
  /* And the exact mirror of it. On-site composites the render onto the
     reference photograph, so with no photograph there is nothing to composite
     onto and nothing for the view to be — which is the manual-setup and screen
     array paths, both of which are specified by numbers rather than measured
     off a shot. It was already refused on click; being refused is a worse
     answer than not being offered, since the button still reads as a place you
     could go. Hidden on the same terms Split is, from the same one place. */
  const siteTab = $('#viewtabs button[data-view="site"]');
  if(siteTab) siteTab.classList.toggle('hidden', !S.photo);
  $('#view3d').classList.toggle('hide', v==='site' || v==='split');
  $('#viewSite').classList.toggle('hide', v!=='site');   // the standalone host; in Split the wrap lives in the pane
  $('#viewSplit').classList.toggle('hide', v!=='split');
  $('#viewCalib').classList.add('hide');
  // #fitbox is the one live, interactive orbit canvas (#cv) — standalone
  // Orbit and Split's left pane share it by physically moving it between
  // containers rather than duplicating it, so every bit of picking/gizmo
  // code that already targets #cv keeps working unchanged in Split too.
  // Moving a DOM node via appendChild preserves its listeners and, for the
  // canvas specifically, its live WebGL context — nothing to rewire.
  const fbHome = $(v==='split' ? '#splitPaneL' : '#view3d');
  if($('#fitbox').parentElement !== fbHome) fbHome.appendChild($('#fitbox'));
  /* The mockup MOVES into the pane, for the reason given just above about
     #fitbox: #siteWrap carries the photograph, four independently warped
     canvases, the reach layer and the pointer handlers bound to it. Reparenting
     keeps every one of them, so all the site drawing and picking below works on
     the same nodes it always did — .splitpane already flex-centres its child
     and #siteWrap is an inline-block capped at 100%, so it simply fits.
     #fitboxSplitR is the Flat pane, stood down while the mockup has the room. */
  const siteSplit = v === 'split' && !!S.photo;
  const swHome = $(siteSplit ? '#splitPaneR' : '#viewSite');
  if($('#siteWrap').parentElement !== swHome) swHome.appendChild($('#siteWrap'));
  $('#fitboxSplitR').classList.toggle('hidden', siteSplit);
  updateHint(); syncGizmoBar();
  if(v!=='site') siteFit();             // leaving On-site drops the zoom
  // Leaving Flat drops its zoom AND its pan — both are preview-only
  // framing, and coming back to a view still scrolled off to one side
  // from last time reads as the render having moved.
  if(v!=='flat' && !(v==='split' && !S.photo)){ S.flatZoom = 1; S.flatPan = { x: 0, y: 0 }; }
  applySiteTransform();
  /* Writes the transform on entry and CLEARS it on the way out — #fitbox is
     reparented into Split's left pane and is Orbit's canvas too, so a scale
     left on it would follow it into views that never asked for one. */
  applyFlatTransform();
  /* Offered wherever something can actually be zoomed — which includes Split,
     because the mockup in its right pane is zoomable there (isSiteLike is true
     for split-with-photo, so the wheel over it works). Leaving the control out
     there was the same fault it exists to fix: a view you can zoom with no
     visible way back to 100%. Split's LEFT pane is Orbit, which has never had
     a zoom of this kind — it dollies instead. */
  /* Split always: its right half is either the mockup or a Flat pane, and both zoom. */
  $('#zoombar').classList.toggle('hidden', !(v === 'flat' || v === 'site' || v === 'split'));
  // Some panels are view-specific — Calibrate's own left panel doesn't
  // read S.view at all (rail-left.js only ever renders Calibrate content
  // there), but Master Settings' Zoom/Viewer panels (rail-right.js) do,
  // and previously only got refreshed on the NEXT unrelated state change —
  // switching view tabs left them showing whatever the last one rendered,
  // including a panel (Viewer/Nudge) that may not even apply any more.
  if(S.stage!=='calib') renderLeft();
  renderRight();
  fitStage(); requestRender();          // fitStage's 'stage:fit' repaints the watermark once #fitbox's size is final
}
document.querySelectorAll('#viewtabs button').forEach(b=>b.onclick=()=>setView(b.dataset.view));

/* One control, two views. Each already owns its own zoom — S.siteZoom scales
   #siteWrap, S.flatZoom scales #fitbox — so this only has to decide which one
   it is talking to. A step of 1.25 per press: the wheel is for fine work, and
   a button that moves in visible jumps is the point of having a button. */
const zoomStep = k => {
  /* isSiteLike, not S.view==='site': the mockup is the thing being zoomed in
     Split too, and it is the same #siteWrap and the same S.siteZoom wherever
     it happens to be parented. */
  if(isSiteLike()) setSiteZoom((S.siteZoom || 1) * k);
  else if(flatZoomable()) setFlatZoom((S.flatZoom || 1) * k);
};
$('#zoomIn').onclick  = () => zoomStep(1.25);
$('#zoomOut').onclick = () => zoomStep(1 / 1.25);
/* The number IS the reset, as it is in the timeline's zoom — pressing the
   thing that tells you where you are to get back to where you started needs
   no second button and no label. */
$('#zoomVal').onclick = () => { if(isSiteLike()) siteFit(); else if(flatZoomable()) flatFit(); };

const GZHINT = {
  select:'Click to select · drag empty space to orbit · right-drag to pan · scroll to zoom',
  move:  'G/R/S · arrows nudge, alt+arrows for depth · ⌘Z undo · F frames selection',
  rotate:'G move · R rotate · S scale · drag a ring · shift = fine, ctrl = snap 5°',
  scale: 'G move · R rotate · S scale · drag a handle, or the cube for uniform · [ ] resize handles'
};
const GZ_LABEL = { select:'Select', move:'Move', rotate:'Rotate', scale:'Scale' };
const GZ_KEY = { select:'V', move:'G', rotate:'R', scale:'S' };
const GZ_ICON = {
  select:'<path d="M3.5 2.2 12 8.4l-3.7.5-1.9 3.4z" fill="none" stroke="currentColor" stroke-width="1.3"/>',
  move:  '<path d="M8 2v12M2 8h12" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 2 6.4 3.8M8 2l1.6 1.8M8 14l-1.6-1.8M8 14l1.6-1.8M2 8l1.8-1.6M2 8l1.8 1.6M14 8l-1.8-1.6M14 8l-1.8 1.6" fill="none" stroke="currentColor" stroke-width="1.3"/>',
  rotate:'<path d="M13 8a5 5 0 1 1-1.8-3.85" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M13.2 1.9v2.9h-2.9" fill="none" stroke="currentColor" stroke-width="1.3"/>',
  scale: '<path d="M3 13 13 3" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M9.4 3H13v3.6" fill="none" stroke="currentColor" stroke-width="1.3"/><rect x="2.4" y="10.4" width="3.2" height="3.2" rx=".5" fill="none" stroke="currentColor" stroke-width="1.3"/>'
};
const MASK_ICON = '<path d="m8 2.4 5.4 3.9-2 6.3H4.6l-2-6.3z" fill="none" stroke="currentColor" stroke-width="1.3"/>';
/* World/Local used to be a text button that swapped its own label. One icon
   now: it shows whichever space is active (globe = World, axis tripod =
   Local) and a click flips both the icon and the state, the way a Caps
   Lock key works rather than a pair of options to pick between. */
const SPACE_ICON = {
  world: '<circle cx="8" cy="8" r="5.6" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M2.4 8h11.2M8 2.4c1.5 1.7 2.2 3.6 2.2 5.6S9.5 12 8 13.6C6.5 12 5.8 10 5.8 8s.7-3.9 2.2-5.6z" fill="none" stroke="currentColor" stroke-width="1.3"/>',
  local: '<path d="M8 8V2M8 8l5 3M8 8 3 11" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="8" cy="2" r=".9" fill="currentColor" stroke="none"/><circle cx="13" cy="11" r=".9" fill="currentColor" stroke="none"/><circle cx="3" cy="11" r=".9" fill="currentColor" stroke="none"/>'
};
/* Object-add tools, in the order the old tile grid used. Reuses the exact
   icon set the tiles read from (ui/controls.js's ICONS) rather than a
   second, hand-duplicated set of paths. */
const ADD_TYPES = ['recess','frame','text','textarea','image','video','model','light'];

/* Icon + label, replacing the old icon-only 32×32 square — a keyboard
   shortcut, when the tool has one, surfaces as a small badge instead of
   staying hidden behind a hover tooltip. .palette itself has no explicit
   width (see app.css) — it shrink-wraps to whichever row's label+key is
   widest, so the palette is only ever as wide as its own longest word,
   nothing hardcoded here or there to keep in sync. */
function toolRow(icon, label, key, viewBox='0 0 16 16'){
  return `<svg viewBox="${viewBox}">${icon}</svg><span class="label">${label}</span>`
    + (key ? `<span class="key">${key}</span>` : '');
}
/* The button set is static — only .on/.disabled states change — so it's
   built once here instead of hand-written in index.html, reusing the same
   icon data the object-add tiles used to. */
function buildPalette(){
  const p = $('#palette');
  // Select is the neutral tool — always available, even with nothing picked
  // (see syncGizmoBar), and deliberately not tinted with Move/Rotate/Scale
  // below: it's not a transform mode, just "nothing is grabbed."
  const selB = el('button', 'tool');
  selB.dataset.gz = 'select';
  selB.title = `${GZ_LABEL.select} (${GZ_KEY.select})`;
  selB.innerHTML = toolRow(GZ_ICON.select, GZ_LABEL.select, GZ_KEY.select);
  selB.onclick = ()=>setGizmoMode('select');
  p.appendChild(selB);
  // Move starts active — matches S.gizmo's default (state/store.js) and
  // keeps placing/dragging an object immediately after adding it a single
  // click, not two (pick a transform tool, then click the object).
  ['move','rotate','scale'].forEach(m=>{
    const b = el('button', 'tool xform'+(m==='move'?' on':''));
    b.dataset.gz = m;
    b.title = `${GZ_LABEL[m]} (${GZ_KEY[m]})`;
    b.innerHTML = toolRow(GZ_ICON[m], GZ_LABEL[m], GZ_KEY[m]);
    b.onclick = ()=>setGizmoMode(m);
    p.appendChild(b);
  });
  const sp = el('button', 'tool space on');
  sp.id = 'gzSpace';
  sp.innerHTML = toolRow(SPACE_ICON.world, 'World space');
  sp.onclick = ()=>{
    S.gizmoSpace = S.gizmoSpace==='world' ? 'local' : 'world';
    paintSpaceBtn(); setHover(null); requestRender();
  };
  p.appendChild(sp);
  /* The divider goes INSIDE the add group, not beside it — Motion hides the
     group (nothing is being built there) and a separator left behind as a
     direct child would hang off the bottom of the palette dividing nothing.
     .palette-mask already carries its own for the same reason. */
  const add = el('div','palette-add');
  add.appendChild(el('div','sep'));
  ADD_TYPES.forEach(t=>{
    const b = el('button','tool');
    b.title = NICE[t];
    if(t==='video') b.id = 'toolVideo';
    b.innerHTML = toolRow(ICONS[t], NICE[t], null, '0 0 17 16');
    b.onclick = ()=>{
      if(t==='image') $('#fileImg').click();
      else if(t==='video'){
        /* Says no HERE as well as in prepareVideo, and for the reason
           arraySetup gives: the refusal that counts is the one deeper down,
           which no path can walk round, and this only spares somebody the
           file picker before it tells them. A file dialog that opens, takes a
           choice, and then refuses is a worse answer than one that never
           opened. */
        const gate = canUseVideo();
        if(!gate.ok){ toast(gate.reason, true, { text: MOTION_CTA, onClick: showTrial }); return; }
        $('#fileVideo').click();
      }
      else if(t==='model') showAddModel();    // import a file, or a basic shape — see ui/add-model.js
      else addObject(t);
    };
    add.appendChild(b);
  });
  p.appendChild(add);
  // Masking only applies to images (see rail-right.js's "Add/Edit mask" row)
  // so — like the add tools above — it belongs to Design, not Export; see
  // goStage. openMask (ui/mask-editor.js) picks On-site's building-aligned
  // editor or the plain 2D modal depending on the current view.
  const maskWrap = el('div','palette-mask');
  maskWrap.appendChild(el('div','sep'));
  const mb = el('button','tool');
  mb.id = 'toolMask';
  mb.title = 'Mask';
  mb.innerHTML = toolRow(MASK_ICON, 'Mask');
  mb.onclick = ()=>{ const o = getSel(); if(o) openMask(o); };
  maskWrap.appendChild(mb);
  p.appendChild(maskWrap);
}
buildPalette();
/* After the palette exists, unlike the two locks above, whose buttons are in
   index.html and are there before any of this runs. */
syncVideoLock();

export function setGizmoMode(m){
  S.gizmo = m;
  document.querySelectorAll('.palette button[data-gz]').forEach(b=>b.classList.toggle('on', b.dataset.gz===m));
  const sp = $('#gzSpace');
  sp.disabled = (m==='scale' || m==='select');
  sp.title = m==='select'
    ? 'Nothing is being transformed, so there’s no space to switch'
    : m==='scale'
    ? 'Scale is applied along the object’s own axes, so this mode is always local'
    : 'Toggle between world and object axes';
  paintSpaceBtn();
  syncGizmoBar();
  setHover(null);
  updateHint();
  requestRender();
}
export function syncGizmoBar(){
  const o = getSel();
  // Select is the neutral tool — usable with nothing picked, unlike Move/
  // Rotate/Scale/World-Local, which all need a selection to have anything
  // to act on. Mask needs a selection too, and specifically an image.
  // The eye/camera counts as "having a selection" for Move only — its own
  // gizmo (scene/gizmo.js's updateGizmo) never shows Rotate or Scale
  // handles for it, so those stay disabled same as with nothing selected.
  document.querySelectorAll('.palette button[data-gz], #gzSpace').forEach(b=>{
    const eyeOk = S.eyeSelected && b.dataset.gz === 'move';
    b.disabled = (b.dataset.gz!=='select' && !o && !eyeOk) || (b.id==='gzSpace' && (S.gizmo==='scale' || S.gizmo==='select'));
  });
  const mb = $('#toolMask');
  if(mb) mb.disabled = !o || o.type!=='image';
}
function paintSpaceBtn(){
  const sp = $('#gzSpace'), local = gzLocal();
  sp.innerHTML = toolRow(local ? SPACE_ICON.local : SPACE_ICON.world, local ? 'Local space' : 'World space');
  sp.title = local ? 'Local space — click for World' : 'World space — click for Local';
  sp.classList.toggle('on', local);
}

const VIEW_HINT = {
  orbit: 'Drag empty space to orbit · right-drag to pan · scroll to zoom · F to frame',
  split: 'Drag empty space to orbit · right-drag to pan · scroll to zoom · the right pane previews the Flat deliverable live',
  flat:  'Click an object to select · scroll to zoom · this view is the deliverable',
  site:  'Drag the handles straight onto the building · right-drag or shift-scroll to pan · scroll to zoom'
};
let hintFadeT;
export function updateHint(){
  const h = $('#hint');
  clearTimeout(hintFadeT);
  if(S.stage==='calib'){ h.textContent=''; return; }
  h.textContent = getSel() ? GZHINT[S.gizmo] : (VIEW_HINT[S.view] || '');
  // A brief reminder when the context actually changes (tool, view,
  // selection) — not a permanent fixture once you already know it.
  h.style.opacity = '1';
  hintFadeT = setTimeout(()=>{ h.style.opacity = '0'; }, 10000);
}
